import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Save, Search, Plus, Trash2, AlertTriangle, CheckCircle, X, Edit, UserCheck, ListChecks } from 'lucide-react';
import MainService from '../../services/MainService';

const STATUS_OPTIONS = [
  { value: 'BOOKED', label: 'Booked' },
  { value: 'ASSIGNED', label: 'Assigned' },
  { value: 'PICKED', label: 'Picked' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'RETURNED', label: 'Returned' },
];

const emptyForm = (requestType) => ({
  Id: 0,
  RequestType: requestType,
  Date: new Date().toISOString().slice(0, 10),
  PreferredPickupDate: '',
  ClientId: '',
  CustomerId: '',
  ContactName: '',
  ContactMobile: '',
  EmirateId: '',
  LocationId: '',
  Address: '',
  Pieces: 1,
  DriverId: '',
  Remarks: '',
  Status: 'BOOKED',
});

/**
 * @param {'CLIENT'|'CUSTOMER'} requestType
 */
const PickupRequestPage = ({ requestType, title, subtitle }) => {
  const isClient = requestType === 'CLIENT';
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [keyword, setKeyword] = useState('');
  const [listEmirateId, setListEmirateId] = useState('');
  const [listLocationId, setListLocationId] = useState('');
  const [listStatus, setListStatus] = useState('');
  const [listLocations, setListLocations] = useState([]);
  const [form, setForm] = useState(() => emptyForm(requestType));
  const [showForm, setShowForm] = useState(false);
  const [emirates, setEmirates] = useState([]);
  const [locations, setLocations] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [partyOptions, setPartyOptions] = useState([]);
  const [partySearch, setPartySearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDriverId, setBulkDriverId] = useState('');
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkRemarks, setBulkRemarks] = useState('');
  const [bulkStatusUpdating, setBulkStatusUpdating] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await MainService.PickupRequestsList({
        RequestType: requestType,
        Keyword: keyword.trim() || null,
        EmirateId: listEmirateId ? Number(listEmirateId) : null,
        LocationId: listLocationId ? Number(listLocationId) : null,
        Status: listStatus || null,
        Skip: 0,
        Take: 200,
      });
      setRows(resp?.Data || (Array.isArray(resp) ? resp : []));
      setTotal(resp?.Total ?? (resp?.Data?.length || 0));
      setSelectedIds([]);
    } catch (err) {
      console.error(err);
      setError('Failed to load pickup requests');
    } finally {
      setLoading(false);
    }
  }, [requestType, keyword, listEmirateId, listLocationId, listStatus]);

  const allSelected = useMemo(
    () => rows.length > 0 && selectedIds.length === rows.length,
    [rows, selectedIds]
  );
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds([]);
    else setSelectedIds(rows.map((r) => r.Id));
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    (async () => {
      try {
        const [em, drv] = await Promise.all([
          MainService.EmiratesInDropDown(),
          MainService.DriversInDropDown(),
        ]);
        setEmirates(Array.isArray(em) ? em : []);
        setDrivers(Array.isArray(drv) ? drv : []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  useEffect(() => {
    if (!form.EmirateId) {
      setLocations([]);
      return;
    }
    (async () => {
      try {
        const locs = await MainService.LocationByEmirateId(form.EmirateId);
        setLocations(Array.isArray(locs) ? locs : []);
      } catch (e) {
        console.error(e);
        setLocations([]);
      }
    })();
  }, [form.EmirateId]);

  useEffect(() => {
    if (!listEmirateId) {
      setListLocations([]);
      return;
    }
    (async () => {
      try {
        const locs = await MainService.LocationByEmirateId(listEmirateId);
        setListLocations(Array.isArray(locs) ? locs : []);
      } catch (e) {
        console.error(e);
        setListLocations([]);
      }
    })();
  }, [listEmirateId]);

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(t);
    }
  }, [error]);
  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(t);
    }
  }, [success]);

  const searchParty = async (term) => {
    setPartySearch(term);
    if (!term || term.trim().length < 2) {
      setPartyOptions([]);
      return;
    }
    try {
      // ClientsInList / CustomersInList include default-address EmirateId / LocationId / Address
      const resp = isClient
        ? await MainService.ClientsInList({ Keyword: term.trim(), Skip: 0, Take: 20 })
        : await MainService.CustomersInList({ Keyword: term.trim(), Skip: 0, Take: 20 });
      const list = resp?.Data || resp?.data || (Array.isArray(resp) ? resp : []);
      setPartyOptions(list);
    } catch (e) {
      console.error(e);
    }
  };

  const selectParty = (row) => {
    // Default address fields are nullable — leave empty when party has no default
    const emirateId = row.EmirateId && Number(row.EmirateId) > 0 ? String(row.EmirateId) : '';
    const locationId = row.LocationId && Number(row.LocationId) > 0 ? String(row.LocationId) : '';
    const address = row.Address || '';
    setForm((p) => ({
      ...p,
      ...(isClient
        ? { ClientId: row.Id, CustomerId: '' }
        : { CustomerId: row.Id, ClientId: '' }),
      ContactName: p.ContactName || row.Name || '',
      ContactMobile: p.ContactMobile || row.MobileNo1 || '',
      EmirateId: emirateId,
      LocationId: locationId,
      Address: address,
    }));
    setPartySearch(row.Name || '');
    setPartyOptions([]);
  };

  const openNew = () => {
    setForm(emptyForm(requestType));
    setPartySearch('');
    setShowForm(true);
  };

  const openEdit = (row) => {
    setForm({
      Id: row.Id,
      RequestType: requestType,
      Date: row.Date ? String(row.Date).slice(0, 10) : new Date().toISOString().slice(0, 10),
      PreferredPickupDate: row.PreferredPickupDate ? String(row.PreferredPickupDate).slice(0, 10) : '',
      ClientId: row.ClientId || '',
      CustomerId: row.CustomerId || '',
      ContactName: row.ContactName || '',
      ContactMobile: row.ContactMobile || '',
      EmirateId: row.EmirateId || '',
      LocationId: row.LocationId || '',
      Address: row.Address || '',
      Pieces: row.Pieces || 1,
      DriverId: row.DriverId || '',
      Remarks: row.Remarks || '',
      Status: row.Status || 'BOOKED',
    });
    setPartySearch(isClient ? row.Client?.Name || '' : row.Customer?.Name || '');
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.ContactName.trim() || !form.ContactMobile.trim()) {
      setError('Contact name and mobile are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        RequestType: requestType,
        ClientId: form.ClientId || null,
        CustomerId: form.CustomerId || null,
        EmirateId: form.EmirateId || null,
        LocationId: form.LocationId || null,
        DriverId: form.DriverId || null,
        PreferredPickupDate: form.PreferredPickupDate || null,
        Pieces: Number(form.Pieces) || 1,
      };
      const resp = await MainService.SavePickupRequest(payload);
      if (resp === 'Success' || resp === true) {
        setSuccess(form.Id ? 'Request updated' : 'Request created');
        setShowForm(false);
        await loadList();
      } else {
        setError(typeof resp === 'string' ? resp : 'Save failed');
      }
    } catch (err) {
      console.error(err);
      setError('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete request ${row.RefNo}?`)) return;
    try {
      const resp = await MainService.DeletePickupRequest(row.Id);
      if (resp === 'Success' || resp === true) {
        setSuccess('Deleted');
        await loadList();
      } else setError('Delete failed');
    } catch (err) {
      setError('Delete failed');
    }
  };

  const updateStatus = async (row, status) => {
    try {
      const resp = await MainService.UpdatePickupRequestStatus({ Id: row.Id, Status: status });
      if (resp === 'Success' || resp === true) {
        const label = STATUS_OPTIONS.find((s) => s.value === status)?.label || status;
        setSuccess(`Status → ${label}`);
        await loadList();
      } else setError(typeof resp === 'string' ? resp : 'Status update failed');
    } catch (err) {
      setError('Status update failed');
    }
  };

  const bulkUpdateStatus = async () => {
    if (selectedIds.length === 0) {
      setError('Select at least one request');
      return;
    }
    if (!bulkStatus) {
      setError('Select a status');
      return;
    }
    setBulkStatusUpdating(true);
    setError('');
    try {
      const resp = await MainService.BulkUpdatePickupRequestStatus({
        Ids: selectedIds,
        Status: bulkStatus,
        Remarks: bulkRemarks,
      });
      if (resp === 'Success' || resp === true) {
        const label = STATUS_OPTIONS.find((s) => s.value === bulkStatus)?.label || bulkStatus;
        setSuccess(`Status updated to ${label} for ${selectedIds.length} request(s)`);
        setBulkStatus('');
        setBulkRemarks('');
        await loadList();
      } else {
        setError(typeof resp === 'string' ? resp : 'Bulk status update failed');
      }
    } catch (err) {
      setError('Bulk status update failed');
    } finally {
      setBulkStatusUpdating(false);
    }
  };

  const assignDriver = async (row, driverId) => {
    try {
      const resp = await MainService.AssignPickupRequestDriver({
        Id: row.Id,
        DriverId: driverId || null,
      });
      if (resp === 'Success' || resp === true) {
        setSuccess(driverId ? 'Driver assigned' : 'Driver cleared');
        await loadList();
      } else setError(typeof resp === 'string' ? resp : 'Driver assign failed');
    } catch (err) {
      setError('Driver assign failed');
    }
  };

  const bulkAssignDriver = async () => {
    if (selectedIds.length === 0) {
      setError('Select at least one request');
      return;
    }
    if (!bulkDriverId) {
      setError('Select a driver to assign');
      return;
    }
    setBulkAssigning(true);
    setError('');
    try {
      const resp = await MainService.BulkAssignPickupRequestDriver({
        Ids: selectedIds,
        DriverId: bulkDriverId,
      });
      if (resp === 'Success' || resp === true) {
        setSuccess(`Driver assigned to ${selectedIds.length} request(s)`);
        setBulkDriverId('');
        await loadList();
      } else {
        setError(typeof resp === 'string' ? resp : 'Bulk assign failed');
      }
    } catch (err) {
      setError('Bulk assign failed');
    } finally {
      setBulkAssigning(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 shadow">
          <AlertTriangle className="h-4 w-4" />
          <span>{error}</span>
          <button type="button" onClick={() => setError('')}><X className="h-4 w-4" /></button>
        </div>
      )}
      {success && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-700 shadow">
          <CheckCircle className="h-4 w-4" />
          <span>{success}</span>
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
            <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={openNew}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm text-white hover:bg-green-700"
          >
            <Plus className="h-4 w-4" />
            New Request
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex flex-wrap items-end gap-2">
          <div className="relative">
            <label className="mb-1 block text-xs font-medium text-gray-600">Search</label>
            <Search className="absolute left-3 bottom-2.5 h-4 w-4 text-gray-400" />
            <input
              className="w-72 rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm"
              placeholder={`${isClient ? 'Client' : 'Customer'} name/mob, emirate, location, status…`}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  loadList();
                }
              }}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Emirate</label>
            <select
              className="min-w-[140px] rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={listEmirateId}
              onChange={(e) => {
                setListEmirateId(e.target.value);
                setListLocationId('');
              }}
            >
              <option value="">All</option>
              {emirates.map((e) => (
                <option key={e.Id} value={e.Id}>{e.Name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Location</label>
            <select
              className="min-w-[140px] rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={listLocationId}
              onChange={(e) => setListLocationId(e.target.value)}
              disabled={!listEmirateId}
            >
              <option value="">All</option>
              {listLocations.map((l) => (
                <option key={l.Id} value={l.Id}>{l.Name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Status</label>
            <select
              className="min-w-[130px] rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={listStatus}
              onChange={(e) => setListStatus(e.target.value)}
            >
              <option value="">All</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={loadList}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white hover:bg-green-700"
          >
            <Search className="h-4 w-4" />
            Search
          </button>
          <button
            type="button"
            onClick={() => {
              setKeyword('');
              setListEmirateId('');
              setListLocationId('');
              setListStatus('');
            }}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Clear
          </button>
          <span className="pb-2 text-sm text-gray-500">{total} request(s)</span>
        </div>

        {selectedIds.length > 0 && (
          <div className="mb-4 space-y-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-700">{selectedIds.length} selected</span>
              <button
                type="button"
                onClick={() => {
                  setSelectedIds([]);
                  setBulkDriverId('');
                  setBulkStatus('');
                  setBulkRemarks('');
                }}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
              >
                Clear selection
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select
                className="min-w-[180px] rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
                value={bulkDriverId}
                onChange={(e) => setBulkDriverId(e.target.value)}
              >
                <option value="">Select driver…</option>
                {drivers.map((d) => (
                  <option key={d.Id} value={d.Id}>{d.Name}</option>
                ))}
              </select>
              <button
                type="button"
                disabled={bulkAssigning || !bulkDriverId}
                onClick={bulkAssignDriver}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-1.5 text-sm text-white disabled:opacity-50"
              >
                {bulkAssigning ? <RefreshCw className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4" />}
                Assign driver
              </button>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Status</label>
                <select
                  className="min-w-[160px] rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
                  value={bulkStatus}
                  onChange={(e) => setBulkStatus(e.target.value)}
                >
                  <option value="">Select status…</option>
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div className="min-w-[220px] flex-1">
                <label className="mb-1 block text-xs font-medium text-gray-600">Remarks</label>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
                  placeholder="Remarks for selected…"
                  value={bulkRemarks}
                  onChange={(e) => setBulkRemarks(e.target.value)}
                />
              </div>
              <button
                type="button"
                disabled={bulkStatusUpdating || !bulkStatus}
                onClick={bulkUpdateStatus}
                className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-1.5 text-sm text-white disabled:opacity-50"
              >
                {bulkStatusUpdating ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ListChecks className="h-4 w-4" />}
                Update status
              </button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2 w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected;
                    }}
                    onChange={toggleSelectAll}
                    title="Select all"
                  />
                </th>
                <th className="px-3 py-2">Ref</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">{isClient ? 'Client' : 'Customer'}</th>
                <th className="px-3 py-2">Contact</th>
                <th className="px-3 py-2">Emirate</th>
                <th className="px-3 py-2">Location</th>
                <th className="px-3 py-2">Preferred pickup</th>
                <th className="px-3 py-2">Pieces</th>
                <th className="px-3 py-2">Driver</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={12} className="px-3 py-8 text-center text-gray-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={12} className="px-3 py-8 text-center text-gray-500">No requests yet</td></tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.Id} className={`hover:bg-gray-50 ${selectedIds.includes(row.Id) ? 'bg-green-50/60' : ''}`}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(row.Id)}
                        onChange={() => toggleSelect(row.Id)}
                      />
                    </td>
                    <td className="px-3 py-2 font-medium">{row.RefNo}</td>
                    <td className="px-3 py-2">{row.Date ? String(row.Date).slice(0, 10) : '—'}</td>
                    <td className="px-3 py-2">
                      <div>{isClient ? row.Client?.Name || '—' : row.Customer?.Name || '—'}</div>
                      <div className="text-xs text-gray-500">
                        {isClient ? row.Client?.MobileNo1 || '' : row.Customer?.MobileNo1 || ''}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div>{row.ContactName}</div>
                      <div className="text-xs text-gray-500">{row.ContactMobile}</div>
                    </td>
                    <td className="px-3 py-2">{row.Emirate?.Name || '—'}</td>
                    <td className="px-3 py-2">{row.Location?.Name || '—'}</td>
                    <td className="px-3 py-2">
                      <div>{row.PreferredPickupDate ? String(row.PreferredPickupDate).slice(0, 10) : '—'}</div>
                      {row.Address ? <div className="max-w-[160px] truncate text-xs text-gray-500" title={row.Address}>{row.Address}</div> : null}
                    </td>
                    <td className="px-3 py-2">{row.Pieces}</td>
                    <td className="px-3 py-2">
                      <select
                        className="min-w-[140px] rounded border border-gray-200 px-2 py-1 text-xs"
                        value={row.DriverId || ''}
                        onChange={(e) => assignDriver(row, e.target.value)}
                      >
                        <option value="">Unassigned</option>
                        {drivers.map((d) => (
                          <option key={d.Id} value={d.Id}>{d.Name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <select
                        className="rounded border border-gray-200 px-2 py-1 text-xs"
                        value={row.Status || 'BOOKED'}
                        onChange={(e) => updateStatus(row, e.target.value)}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-center gap-1">
                        <button type="button" className="rounded p-1.5 text-blue-600 hover:bg-blue-50" onClick={() => openEdit(row)} title="Edit">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button type="button" className="rounded p-1.5 text-red-600 hover:bg-red-50" onClick={() => handleDelete(row)} title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <h3 className="text-lg font-semibold">{form.Id ? 'Edit Request' : 'New Pickup Request'}</h3>
              <button type="button" onClick={() => setShowForm(false)}><X className="h-5 w-5 text-gray-500" /></button>
            </div>
            <form onSubmit={handleSave} className="space-y-4 px-5 py-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-gray-700">{isClient ? 'Client' : 'Customer'}</span>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    value={partySearch}
                    onChange={(e) => searchParty(e.target.value)}
                    placeholder={`Search ${isClient ? 'client' : 'customer'}…`}
                  />
                  {partyOptions.length > 0 && (
                    <ul className="mt-1 max-h-36 overflow-auto rounded border border-gray-200 bg-white shadow">
                      {partyOptions.map((p) => (
                        <li key={p.Id}>
                          <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50" onClick={() => selectParty(p)}>
                            {p.Name} {p.MobileNo1 ? `· ${p.MobileNo1}` : ''}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-gray-700">Preferred pickup date</span>
                  <input type="date" className="w-full rounded-lg border border-gray-300 px-3 py-2" value={form.PreferredPickupDate} onChange={(e) => setForm((p) => ({ ...p, PreferredPickupDate: e.target.value }))} />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-gray-700">Contact name *</span>
                  <input required className="w-full rounded-lg border border-gray-300 px-3 py-2" value={form.ContactName} onChange={(e) => setForm((p) => ({ ...p, ContactName: e.target.value }))} />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-gray-700">Contact mobile *</span>
                  <input required className="w-full rounded-lg border border-gray-300 px-3 py-2" value={form.ContactMobile} onChange={(e) => setForm((p) => ({ ...p, ContactMobile: e.target.value }))} />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-gray-700">Emirate <span className="font-normal text-gray-400">(optional)</span></span>
                  <select className="w-full rounded-lg border border-gray-300 px-3 py-2" value={form.EmirateId} onChange={(e) => setForm((p) => ({ ...p, EmirateId: e.target.value, LocationId: '' }))}>
                    <option value="">None</option>
                    {emirates.map((e) => <option key={e.Id} value={e.Id}>{e.Name}</option>)}
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-gray-700">Location <span className="font-normal text-gray-400">(optional)</span></span>
                  <select className="w-full rounded-lg border border-gray-300 px-3 py-2" value={form.LocationId} onChange={(e) => setForm((p) => ({ ...p, LocationId: e.target.value }))} disabled={!form.EmirateId}>
                    <option value="">None</option>
                    {locations.map((l) => <option key={l.Id} value={l.Id}>{l.Name}</option>)}
                  </select>
                </label>
                <label className="block text-sm md:col-span-2">
                  <span className="mb-1 block font-medium text-gray-700">Address</span>
                  <input className="w-full rounded-lg border border-gray-300 px-3 py-2" value={form.Address} onChange={(e) => setForm((p) => ({ ...p, Address: e.target.value }))} />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-gray-700">Pieces</span>
                  <input type="number" min="1" className="w-full rounded-lg border border-gray-300 px-3 py-2" value={form.Pieces} onChange={(e) => setForm((p) => ({ ...p, Pieces: e.target.value }))} />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-gray-700">Assign driver</span>
                  <select
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    value={form.DriverId}
                    onChange={(e) => setForm((p) => ({ ...p, DriverId: e.target.value }))}
                  >
                    <option value="">Unassigned</option>
                    {drivers.map((d) => (
                      <option key={d.Id} value={d.Id}>{d.Name}</option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-gray-700">Status</span>
                  <select className="w-full rounded-lg border border-gray-300 px-3 py-2" value={form.Status} onChange={(e) => setForm((p) => ({ ...p, Status: e.target.value }))}>
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm md:col-span-2">
                  <span className="mb-1 block font-medium text-gray-700">Remarks</span>
                  <textarea rows={3} className="w-full rounded-lg border border-gray-300 px-3 py-2" value={form.Remarks} onChange={(e) => setForm((p) => ({ ...p, Remarks: e.target.value }))} />
                </label>
              </div>
              <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
                <button type="button" onClick={() => setShowForm(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">Cancel</button>
                <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm text-white disabled:opacity-50">
                  {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PickupRequestPage;